
if (typeof window !== 'undefined' && window.location && window.location.protocol === 'file:') { __webpack_public_path__ = './'; }

import Vue from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import * as Sentry from '@sentry/vue'
import axios from 'axios'
import store from './store'
import { startBatchTimer } from './composables/useAppUsage'
import 'bootstrap/dist/js/bootstrap.min'
import 'bootstrap/dist/css/bootstrap.min.css';
import { App as CapApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import './assets/fonts/icons.js'
import { SafeAreaService } from '@/services/SafeAreaService';
import AndroidSyncCoordinator from './services/AndroidSyncCoordinator'
import DrawerLayout from 'vue-drawer-layout'
import './services/GlobalVariables'
import './services/ProxyHandler'
import VueVirtualScroller from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import VueToggles from 'vue-toggles';
import Loading from 'vue-loading-overlay';
import vSelect from 'vue-select'
import { VueEditor } from "vue2-editor";
import "vue-select/dist/vue-select.css";
import './assets/styles/global.css';



import 'vue-loading-overlay/dist/vue-loading.css';
import VueBarcodeScanner from 'vue-barcode-scanner'
import { migrateLegacyVuexState } from './services/migrateOldState';

let options = {
    sensitivity: 300,
    requiredAttr: true,
    controlSequenceKeys: ['NumLock', 'Clear'],
    callbackAfterTimeout: true // default is false
}
import VueGoodTablePlugin from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css'
import VueCompositionAPI from "@vue/composition-api";
import { installGlobalUUID } from '@/utils/uuid';

import BootstrapVue from 'bootstrap-vue';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
Vue.use(BootstrapVue);

// Global `v-tooltip` directive used by `NavigationBar.vue` (and
// potentially other components). BootstrapVue ships its own
// `v-b-tooltip`, but the existing call sites use the shorter
// `v-tooltip` alias. Register a small wrapper directive so the alias
// resolves without a `Failed to resolve directive: tooltip` warning.
Vue.directive('tooltip', {
  bind(el, binding) {
    if (binding.value) el.setAttribute('title', String(binding.value));
  },
  update(el, binding) {
    if (binding.value) el.setAttribute('title', String(binding.value));
  },
});

Vue.use(VueCompositionAPI);
Vue.use(VueGoodTablePlugin);
Vue.use(VueBarcodeScanner, options);

// Expose SweetAlert2 on the Vue prototype as `$swal` so components that
// follow the `vue-sweetalert2` convention (`this.$swal.fire(...)`)
// work without each component importing `Swal` directly. Stock-transfer
// components (SendingItemList, SendingItemRow, ViewIncomingTransfer,
// SearchItemsToSend) rely on this. Must be set BEFORE `new Vue(...)` is
// instantiated below, otherwise `$swal` is `undefined` in those
// components and the click handler throws
// `TypeError: Cannot read properties of undefined (reading 'fire')`.
import Swal from 'sweetalert2';
Vue.prototype.$swal = Swal;

Vue.component('loading', Loading);
Vue.component('VueToggles', VueToggles);
Vue.use(VueVirtualScroller)
Vue.use(DrawerLayout);
Vue.component('vue-editor', VueEditor)
Vue.component('v-select', vSelect)

Vue.config.productionTip = false;
Vue.config.devtools = false;

/**
 * Removes credentials and payment identifiers from Sentry event payloads.
 * This defence-in-depth scrubber handles nested request data before an event
 * leaves the POS, while Sentry's userInfo collection remains disabled.
 * @param {Object} event Sentry event about to be transmitted.
 * @returns {Object} Sanitized event safe for transmission.
 */
function redactPII(event) {
    const sensitiveKeys = new Set(['pin', 'password', 'token', 'creditcard']);

    const redact = (value) => {
        if (Array.isArray(value)) return value.map(redact);
        if (!value || typeof value !== 'object') return value;

        return Object.keys(value).reduce((result, key) => {
            result[key] = sensitiveKeys.has(key.toLowerCase()) ? '[REDACTED]' : redact(value[key]);
            return result;
        }, {});
    };

    return redact(event);
}

Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
    ],
    dataCollection: {
        userInfo: false,
    },
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    release: process.env.VUE_APP_SENTRY_RELEASE,
    environment: process.env.VUE_APP_SENTRY_ENVIRONMENT || 'production',
    beforeSend: redactPII,
});

/**
 * Single point of coverage for axios errors -> Sentry.
 *
 * Retail POS has 67 files that `import axios from "axios"` and every one
 * uses the DEFAULT axios instance (no `axios.create` anywhere in src/ —
 * verified by grep). A single `axios.interceptors.response.use(...)`
 * registered here catches errors from every consumer without editing the
 * per-service `.catch()` blocks.
 *
 * Without this, axios `.catch()` blocks swallow errors before Sentry's
 * `window.onerror` / `unhandledrejection` / `Vue.config.errorHandler`
 * can see them — CORS, 500s, offline failures silently never reach Sentry.
 *
 * Must be registered AFTER `Sentry.init({...})` so `captureException` is
 * wired up. Re-throws via `Promise.reject(error)` so existing `.catch()`
 * handlers still run.
 */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    Sentry.captureException(error, {
      extra: {
        axiosUrl: error?.config?.url,
        axiosMethod: error?.config?.method?.toUpperCase(),
        axiosStatus: error?.response?.status,
        axiosBaseURL: error?.config?.baseURL,
      },
    });
    return Promise.reject(error);
  }
);

(async () => {
    // OPTIMIZATION: Mount Vue immediately to hide boot splash faster
    // Run storage initialization in background

    // Start storage init in background (don't await).
    // Android: sandbox RW SQLite + optional Lytsoft file backup (see lytsoftDataDir).
    const storageInitPromise = (async () => {
        try {
            const { getStorage } = await import('./services/storageAdapter');
            const storage = await getStorage();

            // Perform one-time migration from SQLite to IDB for Electron
            if (storage && typeof storage.migrateToIDB === 'function') {
                await storage.migrateToIDB();
            }
        } catch (err) {
            console.error('[App] Failed to initialize storage adapter:', err);
        }
    })();

    // Run legacy migration in parallel (don't block Vue mount)
    const migrationPromise = migrateLegacyVuexState().catch(err => {
        console.warn('[App] Legacy migration failed:', err);
    });

    // Apply theme and other settings (non-blocking)
    try {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'light' || savedTheme === 'dark') {
            store.commit('setTheme', savedTheme);
        } else if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            store.commit('setTheme', mq.matches ? 'dark' : 'light');
        } else {
            store.commit('setTheme', 'light');
        }
    } catch (_) {
        store.commit('setTheme', 'light');
    }
    try {
        document.body.classList.add('reduced-motion');
    } catch (e) {
        console.warn('[App] Failed to apply reduced-motion class:', e);
    }

    // Initialize deep link service for auto-login
    // This must be done before Vue mount so it's ready when components initialize
    (async () => {
        try {
            const { DeepLinkService } = await import('./services/deepLinkService');
            const deepLinkService = new DeepLinkService();
            await deepLinkService.initialize({ router, store });
            // Expose for debugging
            window.deepLinkService = deepLinkService;
        } catch (e) {
            console.error('[App] Failed to initialize deep link service:', e);
        }
    })();

    // Mount Vue; static #boot-splash stays until first paint + storage ready
    new Vue({
        router,
        store,
        render: h => h(App),
        async mounted() {
            try {
                if (typeof window.__hideBootSplash === 'function') {
                    window.__hideBootSplash();
                }
            } catch (_) {}

            // Wait for storage to be ready before doing heavy operations
            await storageInitPromise;
            await migrationPromise;

            // Electron: get and log available printers (no-op on web/mobile)
            try {
                if (window && window.electronBridge && typeof window.electronBridge.getPrinters === 'function') {
                    const printers = await window.electronBridge.getPrinters();
                }
            } catch (e) {
                console.error('Failed to fetch printers:', e);
            }

            // Install UUID generator globally
            installGlobalUUID();

            // Hydration is handled by vuex-persist in idbStorage.js
            // Sync service is started in App.vue after hydration completes

            // Install Android Sync Coordinator (foreground/resume-based)
            try {
                AndroidSyncCoordinator.install();
            } catch (e) {
                console.warn('[App] Failed to install AndroidSyncCoordinator:', e);
            }

            // Android hardware back button handling (Capacitor)
            try {
                const isNative = Capacitor?.isNativePlatform?.() === true;
                const platform = Capacitor?.getPlatform?.() || 'web';
                if (isNative && platform === 'android' && CapApp?.addListener) {
                    CapApp.addListener('backButton', ({ canGoBack }) => {
                        const hasHistory = window.history.length > 1;
                        const routeName = this.$route?.name;
                        const rootRoutes = new Set(['pos', 'poslogin', 'viewSale', 'payment', 'viewPayment', 'float']);

                        if (hasHistory || canGoBack) {
                            window.dispatchEvent(new CustomEvent('androidBackButton'));
                            setTimeout(() => { this.$router.back(); }, 0);
                            return;
                        }

                        if (rootRoutes.has(routeName)) {
                            CapApp.minimizeApp?.();
                            return;
                        }

                        CapApp.minimizeApp?.();
                    });
                }
            } catch (e) {
                console.warn('[App] Failed to set up Android back button handler:', e);
            }

            // Start App Usage tracking batch timer (auto-flush every 30 seconds)
            try {
                startBatchTimer()
            } catch (e) {
                console.warn('[App] Failed to start App Usage tracking:', e);
            }
        }
    }).$mount('#app')

    // Initialize Safe Area
    await SafeAreaService.init();
})();
